/**
 * LanguageProvider - Context provider for language state management.
 * Integrates useUserLanguage (data layer) with i18next (UI layer).
 *
 * Usage:
 * Wrap app with LanguageProvider after QueryClientProvider.
 * Use useLanguage() hook in components to access language state.
 */

import { createContext, useContext, useCallback, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SupportedLanguage, DEFAULT_LANGUAGE } from "@/constants/languages";
import { useUserLanguage } from "@/infrastructure/adapters/user-preference";
import { applyOsLanguageDefault, shouldApplyOsLanguage } from "@/utils/os-language";
import { LoadingScreen } from "@/components/elements/Loading/Loading";

/**
 * Hard cap on waiting for the device language, in case the native bridge never settles.
 * Past the cap children render; a late result still applies via i18next.
 */
const OS_LANGUAGE_TIMEOUT_MS = 2000;

interface LanguageContextType {
  /** Current active language code */
  currentLanguage: SupportedLanguage;
  /** Change language with persistence */
  changeLanguage: (lang: SupportedLanguage) => Promise<void>;
  /** True while language is being changed */
  isChanging: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  const { i18n } = useTranslation();
  const { currentLanguage, setLanguage, isUpdating } = useUserLanguage();

  // Gate only when the device language will actually be read — returning users and plain browsers
  // start ready, so they never see the loading screen
  const [isLanguageReady, setIsLanguageReady] = useState(() => !shouldApplyOsLanguage());

  // First boot only: seed the UI language from the device, holding back render so the UI never
  // flashes the fallback (ja). No-op when a stored / server-side preference already exists.
  useEffect(() => {
    let active = true;

    const markReady = () => {
      if (active) setIsLanguageReady(true);
    };

    const timeoutId = setTimeout(markReady, OS_LANGUAGE_TIMEOUT_MS);

    applyOsLanguageDefault()
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(timeoutId);
        markReady();
      });

    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, []);

  // Sync i18next with stored language on mount
  useEffect(() => {
    if (currentLanguage && i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      // setLanguage syncs i18next; it persists + calls the API only when authenticated (on the
      // login screen the choice stays in memory until submit succeeds)
      await setLanguage(lang);
    },
    [setLanguage],
  );

  const value: LanguageContextType = {
    currentLanguage: (currentLanguage as SupportedLanguage) || DEFAULT_LANGUAGE,
    changeLanguage,
    isChanging: isUpdating,
  };

  if (!isLanguageReady) {
    return <LoadingScreen />;
  }

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

/**
 * Hook to access language context.
 * Must be used within LanguageProvider.
 *
 * @returns Language context with currentLanguage, changeLanguage, isChanging
 * @throws Error if used outside LanguageProvider
 *
 * @example
 * ```tsx
 * const { currentLanguage, changeLanguage } = useLanguage();
 *
 * const handleChange = (lang: SupportedLanguage) => {
 *   changeLanguage(lang);
 * };
 * ```
 */
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }

  return context;
};
